/**
 * OrderTrackingLine.tsx — the patient's courier + tracking line on an order row.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: pharmacy dispensing lane
 * (2026-07-29).
 *
 * WHY here and shared: the SAME order row is rendered twice — on the patient
 * dashboard (patients/Dashboard.tsx) and in the portal's My-orders section
 * (patient-portal/OrdersSection.tsx). One component keeps the two from drifting,
 * which matters more than usual because of what this line must NOT say.
 *
 * TRACKING ONLY. The shape it renders carries the courier, the tracking number
 * and when it shipped — and nothing else. It never names the pharmacy, and it
 * can never surface a pharmacy's refusal to dispense: a rejection goes to the
 * GP Service admin team, never to the patient (see `orderStatusCopy`'s
 * `dispense_rejected` case). There is no field on `PatientOrderTracking` that
 * could leak either, so the guarantee is structural.
 *
 * Renders NOTHING when there is no tracking — an order that has not shipped, or
 * an orders revision that does not send the field yet, simply shows no line
 * rather than an empty "Tracking: —".
 *
 * `trackingUrl` is a courier's own tracking page, so the link is opened with
 * `rel="noopener noreferrer"` — a third-party destination never gets a handle
 * on the portal window.
 */
import type { PatientOrderTracking } from '../api/types';
import { formatUtcDate } from '../lib/format';

export function OrderTrackingLine({ tracking }: { tracking?: PatientOrderTracking | null }) {
  if (!tracking) return null;

  const label = `${tracking.courier} · ${tracking.trackingNumber}`;

  return (
    <div style={{ fontSize: '13px', color: 'var(--gps-muted)', marginTop: '2px' }}>
      <span>Sent {formatUtcDate(tracking.dispatchedAt)} — </span>
      {tracking.trackingUrl ? (
        <a
          href={tracking.trackingUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'var(--gps-link)', fontWeight: 600 }}
        >
          Track your order ({label})
        </a>
      ) : (
        <span style={{ color: 'var(--gps-text)', fontWeight: 600 }}>{label}</span>
      )}
    </div>
  );
}
